// Module 11 — Customer Service Advanced (CS-01 to CS-08)
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtPKR, fmtDate, fmtDT } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import { ticketApi } from '../../services/api';
import toast from 'react-hot-toast';
import { Plus, MessageSquare, AlertTriangle, Trash2 } from 'lucide-react';

// ── CS-02/03/04 Escalation & SLA Dashboard ────────────────────────────────────
export function SLADashboardPage() {
  const [stats, setStats]   = useState(null);
  const [breached, setBreached] = useState([]);
  const [loading, setLoad]  = useState(true);

  useEffect(()=>{
    Promise.all([
      api.get('/cs/csat-stats').then(r=>r.data).catch(()=>({ success:false })),
      api.get('/tickets?breached=true&limit=50').then(r=>r.data).catch(()=>({ success:false, data:[] })),
      api.get('/tickets?status=OPEN&limit=100').then(r=>r.data).catch(()=>({ success:false, data:[] })),
    ]).then(([csat, br, open])=>{
      if(csat.success) setStats(csat.data);
      setBreached(br.success ? br.data||[] : []);
    }).finally(()=>setLoad(false));
  },[]);

  const escalate = async (ticketId) => {
    try {
      await api.patch('/tickets/'+ticketId, { priority:'URGENT', status:'IN_PROGRESS' }).then(r=>r.data);
      toast.success('Ticket escalated to URGENT'); setBreached(prev=>prev.filter(t=>t.ticket_id!==ticketId));
    } catch(e) { toast.error('Failed'); }
  };

  return (
    <AppShell title="SLA Dashboard">
      {/* CSAT stats */}
      {stats && (
        <div className="stat-grid" style={{ marginBottom:20 }}>
          <div className="stat-card"><div className="stat-label">CSAT Score</div><div className="stat-value" style={{ color: stats.avg_score>=4?'var(--success)':stats.avg_score>=3?'var(--warning)':'var(--danger)' }}>{parseFloat(stats.avg_score||0).toFixed(1)}/5 ⭐</div></div>
          <div className="stat-card"><div className="stat-label">Total Responses</div><div className="stat-value">{stats.total_responses||0}</div></div>
          <div className="stat-card"><div className="stat-label">Promoters (4-5★)</div><div className="stat-value" style={{ color:'var(--success)' }}>{stats.promoters||0}</div></div>
          <div className="stat-card"><div className="stat-label">Detractors (1-2★)</div><div className="stat-value" style={{ color:'var(--danger)' }}>{stats.detractors||0}</div></div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title" style={{ color:'var(--danger)' }}><AlertTriangle size={14} style={{ marginRight:6, display:'inline' }}/>SLA Breached Tickets</span>
          <span style={{ fontSize:'.78rem', color:'var(--danger)' }}>{breached.length} tickets</span>
        </div>
        <div className="table-wrap">
          {loading ? <Spinner /> : breached.length ? (
            <table>
              <thead><tr><th>Ticket No</th><th>Subject</th><th>Customer</th><th>Priority</th><th>Breached By</th><th>Action</th></tr></thead>
              <tbody>
                {breached.map(t=>(
                  <tr key={t.ticket_id} style={{ background:'#FEF2F2' }}>
                    <td><code style={{ fontSize:'.78rem', fontWeight:700, color:'var(--danger)' }}>{t.ticket_no}</code></td>
                    <td style={{ fontWeight:500, fontSize:'.85rem' }}>{t.subject}</td>
                    <td className="text-sm">{t.customer_name||'—'}</td>
                    <td><span className={`badge badge-${t.priority==='URGENT'?'danger':t.priority==='HIGH'?'warning':'info'}`} style={{ fontSize:'.65rem' }}>{t.priority}</span></td>
                    <td style={{ color:'var(--danger)', fontSize:'.78rem' }}>
                      {t.sla_deadline && Math.round((Date.now()-new Date(t.sla_deadline))/36e5)+'h ago'}
                    </td>
                    <td>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>escalate(t.ticket_id)}>
                        <AlertTriangle size={11}/> Escalate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="✅" title="No SLA breaches" />}
        </div>
      </div>
    </AppShell>
  );
}

// ── CS-07/08 Communication Logs & Chat ────────────────────────────────────────
export function CommLogsPage() {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]   = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoad]    = useState(true);

  const load = useCallback(async () => {
    setLoad(true);
    api.get('/tickets?limit=50&status=OPEN,IN_PROGRESS').then(r=>r.data)
      .then(r=>{ if(r.success) setTickets(r.data||[]); }).finally(()=>setLoad(false));
  },[]);
  useEffect(()=>{ load(); },[load]);

  const openTicket = async (t) => {
    setSelected(t); setDetail(null);
    const r = await api.get('/tickets/'+t.ticket_id).then(r=>r.data).catch(()=>({ success:false }));
    if (r.success) setDetail(r.data);
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    try {
      await api.post('/tickets/'+selected.ticket_id+'/messages', { message }).then(r=>r.data);
      setMessage('');
      const r = await api.get('/tickets/'+selected.ticket_id).then(r=>r.data);
      if (r.success) setDetail(r.data);
    } catch { toast.error('Failed to send'); }
  };

  const STATUS_DOT = { OPEN:'var(--danger)', IN_PROGRESS:'var(--amber)', PENDING_CUSTOMER:'var(--info)', RESOLVED:'var(--success)' };

  return (
    <AppShell title="Communication Logs">
      <div style={{ display:'grid', gridTemplateColumns:'300px 1fr', gap:16, height:'calc(100vh - 180px)' }}>
        {/* Ticket list */}
        <div className="card" style={{ overflow:'hidden', display:'flex', flexDirection:'column' }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--gray-100)', fontWeight:600, fontSize:'.85rem' }}>Active Tickets ({tickets.length})</div>
          <div style={{ overflowY:'auto', flex:1 }}>
            {loading ? <Spinner /> : tickets.map(t=>(
              <div key={t.ticket_id}
                style={{ padding:'10px 14px', borderBottom:'1px solid var(--gray-100)', cursor:'pointer', background:selected?.ticket_id===t.ticket_id?'var(--gray-50)':'white' }}
                onClick={()=>openTicket(t)}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <code style={{ fontSize:'.72rem', fontWeight:700, color:'var(--navy)' }}>{t.ticket_no}</code>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:STATUS_DOT[t.status]||'var(--gray-300)', marginTop:4 }}/>
                </div>
                <div style={{ fontSize:'.8rem', fontWeight:500, marginTop:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</div>
                <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginTop:2 }}>{t.customer_name}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat panel */}
        <div className="card" style={{ display:'flex', flexDirection:'column', overflow:'hidden' }}>
          {selected ? (
            <>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--gray-100)', background:'var(--gray-50)' }}>
                <div style={{ fontWeight:600 }}>{selected.ticket_no} — {selected.subject}</div>
                <div style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>{selected.customer_name} · {selected.category}</div>
              </div>
              <div style={{ flex:1, overflowY:'auto', padding:16, display:'flex', flexDirection:'column', gap:8 }}>
                {detail ? (detail.messages||[]).map(m=>(
                  <div key={m.message_id} style={{
                    alignSelf: m.sender_type==='AGENT'||m.actor_type==='employee'?'flex-end':'flex-start',
                    maxWidth:'75%', padding:'8px 12px', borderRadius:10,
                    background: m.sender_type==='AGENT'||m.actor_type==='employee'?'var(--navy)':'var(--gray-100)',
                    color: m.sender_type==='AGENT'||m.actor_type==='employee'?'white':'var(--gray-800)', fontSize:'.85rem'
                  }}>
                    <div style={{ fontSize:'.68rem', opacity:.7, marginBottom:3 }}>{m.sender_type==='AGENT'||m.actor_type==='employee'?'You (Agent)':'Customer'} · {fmtDT(m.sent_at||m.created_at)}</div>
                    {m.message||m.message_text}
                  </div>
                )) : <Spinner />}
              </div>
              <div style={{ padding:'12px 16px', borderTop:'1px solid var(--gray-100)', display:'flex', gap:8 }}>
                <input className="form-control" placeholder="Type reply…" value={message} onChange={e=>setMessage(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMessage()}/>
                <button className="btn btn-primary" onClick={sendMessage}><MessageSquare size={14}/></button>
              </div>
            </>
          ) : (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--gray-400)' }}>
              <div style={{ textAlign:'center' }}>
                <MessageSquare size={40} style={{ marginBottom:12, opacity:.3 }}/>
                <div>Select a ticket to view conversation</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

// ── CS-05/06 Claims Processing ────────────────────────────────────────────────
export function ClaimsPage() {
  const [claims, setClaims]   = useState([]);
  const [loading, setLoad]    = useState(true);
  const [tab, setTab]         = useState('OPEN');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]       = useState({ trackingId:'', claimType:'DAMAGED', amountClaimed:'', description:'' });
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  const load = useCallback(async () => {
    setLoad(true);
    api.get('/claims?status='+tab+'&limit=50').then(r=>r.data)
      .then(r=>{ if(r.success) setClaims(r.data||[]); }).finally(()=>setLoad(false));
  },[tab]);
  useEffect(()=>{ load(); },[load]);

  const updateClaim = async (claimId, status) => {
    try {
      await api.patch('/claims/'+claimId, { status }).then(r=>r.data);
      toast.success('Claim '+status.toLowerCase()); load();
    } catch(e) { toast.error('Failed'); }
  };

  const create = async () => {
    if (!form.trackingId||!form.amountClaimed) { toast.error('Tracking ID and amount required'); return; }
    try {
      await api.post('/claims', form).then(r=>r.data);
      toast.success('Claim created'); setShowCreate(false); load();
    } catch(e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const STATUS_COLOR = { OPEN:'var(--danger)', INVESTIGATING:'var(--amber)', APPROVED:'var(--success)', REJECTED:'var(--gray-400)', SETTLED:'var(--success)' };

  return (
    <AppShell title="Claims Processing" actions={<button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}><Plus size={14}/> New Claim</button>}>
      <div className="tabs" style={{ marginBottom:16 }}>
        {['OPEN','INVESTIGATING','APPROVED','REJECTED','SETTLED'].map(s=>(
          <div key={s} className={'tab'+(tab===s?' active':'')} onClick={()=>setTab(s)}>{s}</div>
        ))}
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : claims.length ? (
            <table>
              <thead><tr><th>Claim No</th><th>Tracking ID</th><th>Type</th><th>Amount</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {claims.map(c=>(
                  <tr key={c.claim_id}>
                    <td><code style={{ fontSize:'.78rem', fontWeight:700 }}>{c.claim_no}</code></td>
                    <td><code style={{ fontSize:'.78rem', color:'var(--navy)' }}>{c.tracking_id}</code></td>
                    <td><span className="badge badge-info" style={{ fontSize:'.65rem' }}>{c.claim_type}</span></td>
                    <td style={{ fontWeight:600, color:'var(--danger)' }}>{fmtPKR(c.amount_claimed)}</td>
                    <td><span style={{ fontSize:'.72rem', fontWeight:600, color:STATUS_COLOR[c.status]||'var(--gray-400)' }}>{c.status}</span></td>
                    <td className="text-sm">{fmtDate(c.created_at)}</td>
                    <td>
                      {c.status==='OPEN' && (
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-info btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>updateClaim(c.claim_id,'INVESTIGATING')}>Investigate</button>
                          <button className="btn btn-success btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>updateClaim(c.claim_id,'APPROVED')}>Approve</button>
                          <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>updateClaim(c.claim_id,'REJECTED')}>Reject</button>
                        </div>
                      )}
                      {c.status==='APPROVED' && (
                        <button className="btn btn-success btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>updateClaim(c.claim_id,'SETTLED')}>Mark Settled</button>
                      )}
                      {['REJECTED','SETTLED'].includes(c.status) && (
                        <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                          onClick={async()=>{ if(!window.confirm('Archive/delete this claim?')) return; try{ await api.delete('/claims/'+c.claim_id); toast.success('Claim removed'); load(); }catch(e){ toast.error(e.response?.data?.message||'Cannot delete active claim'); } }}>
                          <Trash2 size={11}/>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📋" title={'No '+tab.toLowerCase()+' claims'} />}
        </div>
      </div>
      <Modal open={showCreate} onClose={()=>setShowCreate(false)} title="New Claim">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Tracking ID <span className="req">*</span></label><input className="form-control" placeholder="ECLPK..." value={form.trackingId} onChange={set('trackingId')}/></div>
            <div className="form-group"><label className="form-label">Claim Type</label>
              <select className="form-control" value={form.claimType} onChange={set('claimType')}>
                {['DAMAGED','LOST','DELAYED','WRONG_DELIVERY','OTHER'].map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Amount Claimed (PKR) <span className="req">*</span></label><input className="form-control" type="number" value={form.amountClaimed} onChange={set('amountClaimed')}/></div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-control" rows={3} value={form.description} onChange={set('description')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowCreate(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={create}>Submit Claim</button>
        </div>
      </Modal>
    </AppShell>
  );
}
